import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Stack, Box} from 'views/components';
import {useNavigate, useParams} from 'views/module';

import EventList from './event_list_scene/EventList';
import EventListTitle from './event_list_scene/EventListTitle';
import EventDetailTile from './event_list_scene/tiles/EventDetailTile';
import Popup from './event_list_scene/tiles/Popup';

import moment from 'moment-timezone';
import backend from 'utils/backend';

const EventListView = (props) => {
  const {clientRn, statusProgressWrap: progressWrap} = props;

  const [clientScheduleEvents, setClientScheduleEvents] = React.useState({});
  const [weekOffset, setWeekOffset] = React.useState(0);
  const [selectedEvent, setSelectedEvent] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [model, setModel] = React.useState(null);
  const [refreshTrigger, setRefreshTrigger] = React.useState(0);

  const navigate = useNavigate();
  const params = useParams();

  const eventRn = params.eventRn;

  React.useEffect(() => {
    const event = clientScheduleEvents[eventRn];
    if (!event) {
      setSelectedEvent(null);
      return;
    }
    setSelectedEvent(event);
  }, [eventRn, clientScheduleEvents]);

  React.useEffect(() => {
    progressWrap(async () => {
      setLoading(true);
      const targetDate = moment().add(weekOffset, 'weeks');

      const start = targetDate.clone().startOf('day');
      const end = start.clone().add(6, 'days').endOf('day');
      const isoRange = {
        start: start.toISOString(),
        end: end.toISOString(),
      };

      const output = await backend.client.schedule.loadByIsoRange(clientRn, isoRange, true, true, true);
      const events = output.events.reduce((result, event) => {
        return {
          ...result,
          [event.eventRn]: event,
        };
      }, {});
      setClientScheduleEvents(events);

      setLoading(false);
    }, {debugString: 'CLIENT_SCHEDULE_LOAD_EVENTS'});
  }, [weekOffset, refreshTrigger]);

  const {weekDays, weekRange} = React.useMemo(() => {
    const eventsArray = Object.values(clientScheduleEvents);

    // Group events by day
    const groupedEvents = eventsArray.reduce((acc, event) => {
      const dayKey = moment.tz(event.eventData.startTime, event.eventData.timezone).format('dddd, MMMM D');
      acc[dayKey] = acc[dayKey] || [];
      acc[dayKey].push(event);
      return acc;
    }, {});

    // Sort each day's events by start time
    Object.values(groupedEvents).forEach((dayEvents) => {
      dayEvents.sort(
          (a, b) => moment(a.eventData.startTime) - moment(b.eventData.startTime),
      );
    });

    // Find the start and end of each week
    const start = moment().add(weekOffset, 'weeks');
    const end = start.clone().add(6, 'days');
    const weekRange = `${start.format('MMM D')} - ${end.format('MMM D')}`;

    // Build an array for each day in the week, with its events
    const weekDays = Array.from({length: 7}, (_, i) => {
      const dayDate = start.clone().add(i, 'days');
      const dayKey = dayDate.format('dddd, MMMM D');
      return {
        dayKey,
        date: dayDate,
        events: groupedEvents[dayKey] || [],
      };
    });

    return {weekDays, weekRange};
  }, [clientScheduleEvents, weekOffset]);

  const eventHandleList = {
    handleEventClick: (event) => {
      const eventRnClicked = event.eventRn;
      if (!selectedEvent || selectedEvent.eventRn !== eventRnClicked) {
        navigate(`/events/${eventRnClicked}`);
      } else {
        navigate('/events');
      }
    },
    handleWeekNavigation: (direction) => {
      setWeekOffset((prev) => prev + direction);
      navigate('/events');
    },
  };

  const handleCancelEvent = async (clientRn, eventRn, reasonForException, onSuccess=() => undefined) => {
    progressWrap(async () => {
      await backend.client.schedule.cancelEvent(clientRn, eventRn, reasonForException);
      navigate(-1);
      setRefreshTrigger((prev) => prev + 1);
      onSuccess();
    });
  };

  const handleRescheduleEvent = async (clientRn, eventRn, reasonForException, eventData, onSuccess=() => undefined) => {
    progressWrap(async () => {
      const response = await backend.client.schedule.rescheduleEvent(clientRn, eventRn, reasonForException, eventData);

      setRefreshTrigger((prev) => prev + 1);

      navigate(`/events/${response.eventRn}`);

      onSuccess();
    });
  };

  return (
    <Stack sx={{p: {xs: 0, lg: 2}, m: {xs: 0, lg: 0}}}>
      <EventListTitle
        weekRange={weekRange}
        handleWeekNavigation={eventHandleList.handleWeekNavigation}
        loading={loading}
      />
      <Stack
        direction={{xs: 'column', md: 'row'}}
        spacing={0}
        sx={{p: {xs: 0, lg: 2}, m: {xs: 0, lg: 0}, width: '100%', height: '100%'}}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: {
              xs: selectedEvent ? 'none' : 'block',
              md: 'block',
            },
            p: {xs: 0, lg: 2},
            m: {xs: 0, lg: 0},
          }}
        >
          <EventList
            weekDays={weekDays}
            loading={loading}
            selectedEvent={selectedEvent}
            handleEventClick={eventHandleList.handleEventClick}
          />
        </Box>
        {
          selectedEvent && (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: {
                  xs: !selectedEvent ? 'none' : 'block',
                  md: 'block',
                },
                p: {xs: 0, lg: 2},
                m: {xs: 0, lg: 0},
              }}
            >
              <Popup
                model={model}
                setModel={setModel}
                event={selectedEvent}
                handleCancelEvent={handleCancelEvent}
                handleRescheduleEvent={handleRescheduleEvent}
              />
              <EventDetailTile
                setModel={setModel}
                event={selectedEvent}
                statusProgressWrap={progressWrap}
              />
            </Box>
          )
        }
      </Stack>
    </Stack>
  );
};

EventListView.propTypes = {
  clientRn: PropTypes.string.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  clientRn: state.user.clientRn,
});

export default connect(mapStateToProps, dispatch)(EventListView);
